import type { BellSignal, BowlSound } from './types';

type AudioContextLike = AudioContext;

export const BOWL_SOUNDS: BowlSound[] = [
  {
    id: 'soft-dawn',
    name: 'Soft Dawn',
    description: 'Low, warm, slow bloom',
    fundamentalHz: 174,
    decaySeconds: 6.8,
    partials: [
      { multiplier: 1, gain: 0.88 },
      { multiplier: 2.01, gain: 0.24, detuneCents: -4 },
      { multiplier: 2.74, gain: 0.14, detuneCents: 6 },
      { multiplier: 4.18, gain: 0.08 }
    ]
  },
  {
    id: 'deep-bronze',
    name: 'Deep Bronze',
    description: 'Grounded, darker resonance',
    fundamentalHz: 132,
    decaySeconds: 8.5,
    partials: [
      { multiplier: 1, gain: 0.95 },
      { multiplier: 1.52, gain: 0.2, detuneCents: 5 },
      { multiplier: 2.42, gain: 0.16, detuneCents: -7 },
      { multiplier: 3.92, gain: 0.07 }
    ]
  },
  {
    id: 'bright-rim',
    name: 'Bright Rim',
    description: 'Clear, higher shimmer',
    fundamentalHz: 246,
    decaySeconds: 5.6,
    partials: [
      { multiplier: 1, gain: 0.78 },
      { multiplier: 1.98, gain: 0.28, detuneCents: 8 },
      { multiplier: 3.01, gain: 0.15, detuneCents: -5 },
      { multiplier: 5.2, gain: 0.06 }
    ]
  }
];

let audioContext: AudioContextLike | null = null;

export function getBowlSound(id: string): BowlSound {
  return BOWL_SOUNDS.find((sound) => sound.id === id) ?? BOWL_SOUNDS[0];
}

export async function ensureAudioContext(): Promise<AudioContextLike> {
  const AudioCtor = window.AudioContext ?? window.webkitAudioContext;
  if (!AudioCtor) {
    throw new Error('Web Audio is not available in this browser.');
  }
  if (!audioContext) {
    audioContext = new AudioCtor();
  }
  if (audioContext.state === 'suspended') {
    await audioContext.resume();
  }
  return audioContext;
}

export async function playBowlStrike(sound: BowlSound, volume: number, delaySeconds = 0): Promise<void> {
  const context = await ensureAudioContext();
  const startAt = context.currentTime + Math.max(0, delaySeconds);
  const master = context.createGain();
  const safeVolume = Math.min(1, Math.max(0, volume));

  master.gain.setValueAtTime(0.0001, startAt);
  master.gain.exponentialRampToValueAtTime(Math.max(0.0001, safeVolume * 0.72), startAt + 0.035);
  master.gain.exponentialRampToValueAtTime(0.0001, startAt + sound.decaySeconds);
  master.connect(context.destination);

  for (const partial of sound.partials) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = partial.multiplier < 1.1 ? 'sine' : 'triangle';
    oscillator.frequency.setValueAtTime(sound.fundamentalHz * partial.multiplier, startAt);
    if (partial.detuneCents) {
      oscillator.detune.setValueAtTime(partial.detuneCents, startAt);
    }
    gain.gain.setValueAtTime(Math.max(0.0001, partial.gain), startAt);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + sound.decaySeconds * 0.95);
    oscillator.connect(gain);
    gain.connect(master);
    oscillator.start(startAt);
    oscillator.stop(startAt + sound.decaySeconds + 0.1);
  }

  addSoftStrikeNoise(context, master, startAt, safeVolume);
}

export async function playBellSignal(
  signal: BellSignal,
  sound: BowlSound,
  volume: number,
  onStrike?: (signal: BellSignal) => void
): Promise<void> {
  onStrike?.(signal);
  await playBowlStrike(sound, volume, 0);
  if (signal === 'final') {
    window.setTimeout(() => onStrike?.(signal), 850);
    await playBowlStrike(sound, volume, 0.85);
  }
}

function addSoftStrikeNoise(
  context: AudioContextLike,
  destination: GainNode,
  startAt: number,
  volume: number
): void {
  const frameCount = Math.floor(context.sampleRate * 0.09);
  const buffer = context.createBuffer(1, frameCount, context.sampleRate);
  const data = buffer.getChannelData(0);
  for (let index = 0; index < frameCount; index += 1) {
    data[index] = (Math.random() * 2 - 1) * (1 - index / frameCount);
  }

  const noise = context.createBufferSource();
  const filter = context.createBiquadFilter();
  const gain = context.createGain();
  noise.buffer = buffer;
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(920, startAt);
  filter.Q.setValueAtTime(0.85, startAt);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume * 0.07), startAt + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.1);
  noise.connect(filter);
  filter.connect(gain);
  gain.connect(destination);
  noise.start(startAt);
  noise.stop(startAt + 0.11);
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}
